'use client';
import React from 'react';
import { Clock, Sun, Sunrise, Sunset, Wind, Zap } from 'lucide-react';
import { DailySoaringForecast, HourlyCondition } from '@/lib/mockWeatherData';
import { useSettings } from '@/context/SettingsContext';

interface PilotForecastMatrixProps {
  forecasts: DailySoaringForecast[];
}

const HOURS = [10, 11, 12, 13, 14, 15, 16, 17, 18];

function hourData(day: DailySoaringForecast, hour: number): HourlyCondition | undefined {
  return day.hourly?.find((h) => h.time === hour);
}

function dpvFromHour(day: DailySoaringForecast, hour: number): number {
  const remaining = (day.hourly ?? []).filter((h) => h.isConvective && h.time >= hour);
  const current = hourData(day, hour);
  const visibleSoaringHour =
    current?.isConvective &&
    (current.vz ?? 0) >= 1.2 &&
    (current.cumulusBase ?? day.maxCumulusBase) >= 700 &&
    current.windSpeed <= 28;
  if ((day.qualityScore < 10 && !visibleSoaringHour) || day.verdict === 'unflyable') return 0;
  if (!current?.isConvective || remaining.length === 0 || (current.vz ?? 0) <= 0) return 0;

  const ceilingFactor = Math.min(1.35, Math.max(0.6, (current.cumulusBase ?? day.maxCumulusBase) / 1400));
  const timeFactor = Math.min(1.25, remaining.length / 4);
  const windPenalty = current.windSpeed > 28 ? 0.72 : current.windSpeed > 20 ? 0.88 : 1;
  return Math.round((current.vz ?? 0) * 28 * ceilingFactor * timeFactor * windPenalty);
}

function cloudCell(percent?: number): React.ReactNode {
  const value = percent ?? 0;
  if (value < 15) return <span className="block h-3" />;
  const width = value > 70 ? 'w-9' : value > 45 ? 'w-8' : 'w-6';
  const bg = value > 70 ? '#6b7280' : value > 45 ? '#9ca3af' : '#cbd5e1';
  return <span className={`mx-auto block h-2.5 ${width} rounded-sm`} style={{ backgroundColor: bg }} />;
}

function cloudLayerRange(layer: 'high' | 'mid' | 'low', altitudeUnit: 'm' | 'ft'): string {
  const ranges = {
    m: {
      low: '0-2 000 m',
      mid: '2 000-6 000 m',
      high: '> 6 000 m',
    },
    ft: {
      low: '0-6 500 ft',
      mid: '6 500-20 000 ft',
      high: '> 20 000 ft',
    },
  };
  return ranges[altitudeUnit][layer];
}

function cumulusOctas(h?: HourlyCondition): string {
  if (!h?.isConvective) return '';
  if (h.skyType !== 'cumulus') return '';
  const cloud = h.cloudCoverLow ?? 0;
  if (cloud >= 65) return '5/8';
  if (cloud >= 45) return '4/8';
  if (cloud >= 25) return '3/8';
  return '2/8';
}

function weatherText(h?: HourlyCondition): string {
  if (!h?.isConvective) return '';
  if (h.precipProbability && h.precipProbability >= 35) return 'Averses possibles';
  if (h.pilotSkySignal && !['Stable'].includes(h.pilotSkySignal)) return h.pilotSkySignal;
  if (h.skyType === 'cumulus') return 'Cumulus humilis';
  if (h.skyType === 'blue') return 'Thermiques purs';
  return '';
}

function vzDiagnostic(h?: HourlyCondition): string {
  if (!h?.isConvective) return '';
  const sourceMap: Record<string, string> = {
    emagram: 'E',
    energy: 'S',
    cumulus: 'Cu',
    none: '-',
  };
  const source = sourceMap[h.vzReason ?? ''] ?? '?';
  return `${source} ${h.vzEmagram ?? 0}/${h.vzEnergy ?? 0}/${h.vzCumulus ?? 0}`;
}

function maxHourlyVz(day: DailySoaringForecast): number {
  return Math.max(0, ...(day.hourly ?? []).map((h) => h.vz ?? 0));
}

function verdictText(day: DailySoaringForecast): string {
  const maxVz = maxHourlyVz(day);
  if (day.precipTotalMm && day.precipTotalMm >= 5) return 'Pluie probable';
  if (day.qualityScore > 75) return 'Journee excellente';
  if (day.qualityScore >= 50) return 'Bonne journee';
  if (day.qualityScore >= 15) return 'Journee moyenne';
  if (maxVz >= 1.7 && day.maxCumulusBase >= 900) return 'Bon local';
  if (maxVz >= 1.0 && day.maxCumulusBase >= 700) return 'Vol local';
  if (maxVz >= 0.6) return 'Journee moyenne';
  return 'Peu exploitable';
}

export default function PilotForecastMatrix({ forecasts }: PilotForecastMatrixProps) {
  const { settings, formatVZ, formatWind, formatTemp } = useSettings();

  return (
    <div className="space-y-4">
      {forecasts.map((day, idx) => {
        const maxVz = maxHourlyVz(day);
        const maxVzFmt = maxVz > 0 ? formatVZ(maxVz) : null;
        const windSurfaceFmt = formatWind(day.windSurface.speed);
        const hourlyBestDpv = Math.max(0, ...HOURS.map((h) => dpvFromHour(day, h)));
        const bestDpv = ((day.qualityScore < 10 && hourlyBestDpv === 0) || day.verdict === 'unflyable')
          ? 0
          : Math.max(hourlyBestDpv, Math.min(day.dpvKm ?? 0, Math.round(hourlyBestDpv * 1.15)));
        return (
          <section
            key={`pilot-matrix-${day.date}`}
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
          >
            <div
              className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-4 py-3"
              style={{ backgroundColor: idx === 0 ? 'color-mix(in srgb, var(--info-blue) 10%, var(--surface-elevated))' : 'var(--surface-elevated)', borderBottom: '1px solid var(--border)' }}
            >
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold capitalize" style={{ color: 'var(--text-primary)' }}>
                    {idx === 0 ? "Aujourd'hui" : day.dayLabel}
                  </h2>
                  <span className="font-mono-data text-xs" style={{ color: 'var(--text-secondary)' }}>{day.date}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {verdictText(day)} - Score {day.qualityScore}/100 - {day.modelSourceLabel ?? 'Open-Meteo'}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'color-mix(in srgb, var(--warning-orange) 12%, var(--card))', color: 'var(--warning-orange)' }}>
                  <Sun size={13} /> DPV {bestDpv || '--'} km
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'color-mix(in srgb, var(--success-green) 12%, var(--card))', color: 'var(--success-green)' }}>
                  <Zap size={13} /> VZ {maxVzFmt ? `${maxVzFmt.primary} ${maxVzFmt.primaryUnit}` : '--'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'color-mix(in srgb, var(--info-blue) 12%, var(--card))', color: 'var(--info-blue)' }}>
                  <Wind size={13} /> {windSurfaceFmt.primary} {windSurfaceFmt.primaryUnit} {day.windSurface.dir}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'color-mix(in srgb, var(--text-muted) 12%, var(--card))', color: 'var(--text-secondary)' }}>
                  <Sunrise size={13} /> {day.sunrise} <Sunset size={13} /> {day.sunset}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 w-44 px-3 py-2 text-left text-xs uppercase tracking-wider" style={{ background: 'var(--surface)', color: 'var(--info-blue)' }}>
                      <Clock size={13} className="inline mr-1" /> Heure locale
                    </th>
                    {HOURS.map((h) => (
                      <th key={`${day.date}-h-${h}`} className="px-3 py-2 text-center font-mono-data" style={{ color: 'var(--text-primary)', borderLeft: '1px solid var(--border)' }}>
                        {h}h
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: 'DPV restante (km)',
                      render: (h: number) => {
                        const dpv = dpvFromHour(day, h);
                        return dpv > 0 ? dpv.toString() : '0';
                      },
                      color: 'var(--info-blue)',
                    },
                    {
                      label: `Thermiques (${settings.vzUnit})`,
                      render: (h: number) => {
                        const vz = hourData(day, h)?.vz ?? 0;
                        if (vz <= 0) return '0';
                        const fmt = formatVZ(vz);
                        return fmt.primary.replace('.0', '');
                      },
                      color: 'var(--success-green)',
                    },
                    {
                      label: 'Diagnostic VZ',
                      render: (h: number) => vzDiagnostic(hourData(day, h)),
                      color: 'var(--text-muted)',
                    },
                    {
                      label: `Nuages élevés (${cloudLayerRange('high', settings.altitudeUnit)})`,
                      renderNode: (h: number) => cloudCell(hourData(day, h)?.cloudCoverHigh),
                    },
                    {
                      label: `Nuages moyens (${cloudLayerRange('mid', settings.altitudeUnit)})`,
                      renderNode: (h: number) => cloudCell(hourData(day, h)?.cloudCoverMid),
                    },
                    {
                      label: `Nuages bas (${cloudLayerRange('low', settings.altitudeUnit)})`,
                      renderNode: (h: number) => cloudCell(hourData(day, h)?.cloudCoverLow),
                    },
                    {
                      label: 'Octas cumulus',
                      render: (h: number) => cumulusOctas(hourData(day, h)),
                      color: 'var(--purple-cloud)',
                    },
                    {
                      label: `Température (${settings.tempUnit})`,
                      render: (h: number) => {
                        const t = hourData(day, h)?.temp;
                        return typeof t === 'number' ? formatTemp(t).primary : '';
                      },
                      color: 'var(--warning-orange)',
                    },
                    {
                      label: `Vent (${settings.windUnit})`,
                      render: (h: number) => {
                        const row = hourData(day, h);
                        return row ? `${formatWind(row.windSpeed).primary}\n${row.windDir}°` : '';
                      },
                      color: 'var(--text-primary)',
                    },
                    {
                      label: 'Temps',
                      render: (h: number) => weatherText(hourData(day, h)),
                      color: 'var(--text-secondary)',
                    },
                  ].map((row) => (
                    <tr key={`${day.date}-${row.label}`} style={{ borderTop: '1px solid var(--border)' }}>
                      <td className="sticky left-0 z-10 px-3 py-2 text-xs font-semibold" style={{ background: 'var(--surface)', color: 'var(--text-primary)' }}>
                        {row.label}
                      </td>
                      {HOURS.map((h) => (
                        <td key={`${day.date}-${row.label}-${h}`} className="px-3 py-2 text-center whitespace-pre-line" style={{ borderLeft: '1px solid var(--border)', color: row.color ?? 'var(--text-primary)' }}>
                          {'renderNode' in row && row.renderNode ? row.renderNode(h) : row.render?.(h)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
